import { z } from "zod";
/**
 * Core skill categories a student can tag their EOI with. Top-level only
 * for now -- no sub-categories yet, and this list is expected to change with
 * feedback, which is why it lives in `answers` rather than a real column.
 */
export const SKILL_CATEGORIES = [
    "PCB Design",
    "Python",
    "Web Design",
    "3D/CAD Design",
    "FPGAs",
    "Microcontrollers",
    "Mathematical Modelling",
    "RF Design",
    "Computer Networks",
];
/**
 * Condensed for now: BE(Hons) Part I-IV only, not the 1-8 range the schema
 * used to allow. Postgrad and non-BE programmes don't fit yet -- revisit
 * once the real programme list is settled, alongside the enum in
 * schema.prisma, which this must be kept in sync with by hand.
 */
export const YEARS_OF_STUDY = ["PART1", "PART2", "PART3", "PART4"];
export const YEAR_OF_STUDY_LABELS = {
    PART1: "Part 1",
    PART2: "Part 2",
    PART3: "Part 3",
    PART4: "Part 4",
};
/**
 * Replaces the old freeform "programme of study" text field. Fixed to this
 * department's three specialisations -- there is no longer a general
 * programme field at all, by design, not just condensed like year of study.
 */
export const SPECIALISATIONS = ["CSE", "SWE", "EEE"];
export const SPECIALISATION_LABELS = {
    CSE: "Computer Systems Engineering",
    SWE: "Software Engineering",
    EEE: "Electrical & Electronics Engineering",
};
/** Condensed form for compact UI, e.g. the segmented control on the EOI form. */
export const SPECIALISATION_SHORT_LABELS = {
    CSE: "CSE",
    SWE: "SE",
    EEE: "EEE",
};
/**
 * How much time a student can commit to a placement. Fixed options, but it
 * lives in `answers` rather than getting a real column: nothing queries or
 * sorts on it yet, which is the bar for promoting a field. Moving it later
 * means a migration plus an enum in schema.prisma, the same way
 * specialisation and year of study went.
 */
export const AVAILABILITIES = ["FULL_TIME", "PART_TIME", "CASUAL"];
export const AVAILABILITY_LABELS = {
    FULL_TIME: "Full Time",
    PART_TIME: "Part Time",
    CASUAL: "Casual/Temporary",
};
/**
 * Core student fields we are confident about. The real EOI form has more
 * fields that the client is still collecting -- those land in `answers`.
 * Most keys there are unvalidated until the client's field list settles, but
 * a key can get its own strict rule as soon as we do know its shape, like
 * `skills` below -- `.passthrough()` leaves every other key alone.
 */
/** Free text, unlike skills -- just bounded so the array can't be abused. */
const hobbySchema = z.string().trim().min(1).max(40);
/** Same idea as hobbies, just a longer cap -- "AWS Certified Cloud Practitioner" doesn't fit in 40. */
const achievementSchema = z.string().trim().min(1).max(60);
/**
 * "About me" is the student's own pitch and is now the primary thing a company
 * reads -- the CV is an optional attachment to it. Capped at roughly one page
 * of prose: ~500 words, which is where 3000 characters lands.
 */
export const ABOUT_ME_MAX = 3000;
export const studentAnswersSchema = z
    .object({
    aboutMe: z.string().trim().max(ABOUT_ME_MAX).optional(),
    skills: z.array(z.enum(SKILL_CATEGORIES)).max(SKILL_CATEGORIES.length).optional(),
    hobbies: z.array(hobbySchema).max(20).optional(),
    achievements: z.array(achievementSchema).max(15).optional(),
    availability: z.enum(AVAILABILITIES).optional(),
})
    .passthrough();
export const studentProfileSchema = z.object({
    firstName: z.string().trim().min(1).max(100),
    lastName: z.string().trim().min(1).max(100),
    studentId: z.string().trim().regex(/^\d{6,10}$/, "Student ID must be 6-10 digits"),
    phone: z.string().trim().max(30).optional().or(z.literal("")),
    specialisation: z.enum(SPECIALISATIONS),
    yearOfStudy: z.enum(YEARS_OF_STUDY),
    answers: studentAnswersSchema.default({}),
});
export const applicationSchema = z.object({
    positionId: z.string().uuid(),
    coverNote: z.string().trim().max(4000).optional().or(z.literal("")),
});
/*
 * There is no ApplicationStatus. A student applying needs no approval, so an
 * application either exists or it does not -- see the comment on the
 * Application model in schema.prisma.
 */
